import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { User, Calendar, Clock } from 'lucide-react';
import './BlogPreview.css';

interface BlogPostPreviewProps {
  title: string;
  excerpt: string;
  content: string;
  tags: string[];
  image: string;
  author: string;
}

const BlogPostPreview = ({
  title,
  excerpt,
  content,
  tags,
  image,
  author,
}: BlogPostPreviewProps) => {
  // Create a function to safely render HTML content
  const createMarkup = () => {
    return { __html: content };
  };

  // Calculate estimated read time (1 min per 200 words)
  const calculateReadTime = () => {
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readTime = Math.ceil(wordCount / 200);
    return readTime > 0 ? readTime : 1; // Minimum 1 minute
  };

  const readTime = calculateReadTime();

  return (
    <div className="preview-card overflow-hidden border-0 shadow-none bg-white">
      <div className="pb-0 text-center mb-4">
        <h2 className="text-2xl font-bold">Pré-visualização</h2>
      </div>
      <div className="overflow-y-auto max-h-[70vh]">
        <div className="pt-4 pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            {/* Post header */}
            <header className="mb-10">
              {/* Featured image - Full width with appropriate dimensions */}
              {image && (
                <div className="w-full mb-10 rounded-xl overflow-hidden shadow-md">
                  <img 
                    src={image} 
                    alt={title} 
                    className="w-full h-auto object-cover" 
                    style={{ maxHeight: '500px' }}
                  />
                </div>
              )}
            
              <div className="max-w-4xl mx-auto">
                {/* Post tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {tags.filter(tag => tag).map((tag, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary"
                      className="bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-800/40 px-3 py-1 text-sm font-medium shadow-sm"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
                
                {/* Post title */}
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">{title || 'Título do Post'}</h1>
                
                {/* Post metadata (author, date, read time) */}
                <div className="flex flex-wrap items-center gap-6 text-gray-600 text-sm border-b border-gray-200 pb-6">
                  <div className="flex items-center">
                    <User size={16} className="mr-2" />
                    <span>{author || 'Autor'}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar size={16} className="mr-2" />
                    <span>
                      {new Date().toLocaleDateString('pt-BR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={16} className="mr-2" />
                    <span>{readTime} min de leitura</span>
                  </div>
                </div>

                {/* Excerpt (if available) */}
                {excerpt && (
                  <div className="my-6 text-lg text-gray-600 italic border-l-4 border-blue-500 pl-4 py-2">
                    {excerpt}
                  </div>
                )}
              </div>
            </header>

            {/* Post content */}
            <div className="max-w-4xl mx-auto">
              <article className="prose prose-lg prose-blue max-w-none text-left bg-white p-6 md:p-8 rounded-xl border border-gray-100 shadow-sm">
                <div 
                  dangerouslySetInnerHTML={createMarkup()} 
                  className="text-gray-700 mb-6 leading-relaxed"
                />
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPreview;
